import { reflectKeys } from "@utils/key.reflactor.ts";

export const NAMESPACE = {
  ANNOTATION: "annotation",
  APP: "app",
  AUTH: "auth",
  ATTEMPT: "attempt",
  RESOURCE: "attempt/resource",
  COMPONENT_ATTEMPT: "attempt/componentAttempt",
  MONITORING: "attempt/monitoring",
};

export const RESOURCE_DOMAIN = {
  MOE: "MOE",
  COMMUNITY: "COMMUNITY",
  PERSONAL: "PERSONAL",
  ALP_TEMPLATE: "ALP_TEMPLATE",
  ASSIGNMENT: "ASSIGNMENT",
};
export type ResourceDomain = (typeof RESOURCE_DOMAIN)[keyof typeof RESOURCE_DOMAIN];

export const COMPONENT_MODE = reflectKeys(["ATTEMPT", "RESULT"]);
export const COMPONENT_TYPE = {
  ACCORDION: "CONTAINER_ACCORDION",
  TWO_COLUMN: "CONTAINER_TWO_COLUMN",
  THREE_COLUMN: "CONTAINER_THREE_COLUMN",
  QUESTION: "COMPONENT_QUESTION",
  TEXT: "COMPONENT_RICHTEXT",
  YOUTUBE: "COMPONENT_YOUTUBE",
  MO: "COMPONENT_MEDIAOBJECT",
  POLL: "COMPONENT_POLL",
  TOPIC: "COMPONENT_TOPIC",
};
export type ComponentType = (typeof COMPONENT_TYPE)[keyof typeof COMPONENT_TYPE];

export const COMPONENT_ATTR = {
  DATA_LOADED: "data-loaded",
};
export const QUERY_PARAM = {
  QUIZ_PAGE: "quizPage",
  QUESTION_LABEL: "question",
  ANCHOR: "anchor",
  QUESTION: "question", //questionUuid (eg. feedback notification anchoring)
  COMPONENT: "component", //componentId
  PAGE_NO: "pageNo", // paginated page number
};

export const QUIZ_EVENT = {
  QUIZ_PAGE_UPDATE_TRIGGERED: "quizPageUpdateTriggered",
  QUIZ_ACTIVITY_PAGE_UPDATE_TRIGGERED: "quizActivityPageUpdateTriggered",
  QUIZ_ACTIVITY_NAVIGATION_TRIGGERED: "quizActivityNavigationTriggered",
};

export const QUIZ_PAGE = {
  QUIZ_COVER: "cover",
  QUIZ_END: "end",
};

export const ATTEMPT_ANSWER_MAX_LENGTH = 32500;

// TODO: to unify and regression test. there are multiple definitions of resource types/statuses
export const RESOURCE_TYPE = reflectKeys(["LESSON", "QUIZ"]);
export const RESOURCE_STATUS = reflectKeys([
  "APPROVED",
  "DRAFT",
  "PENDING_APPROVAL",
  "PENDING_UNPUBLISH",
  "UNPUBLISHED",
  "REJECTED",
  "ARCHIVED",
]);
// TODO: to unify and regression test. also exists in types/question.ts
export const QUESTION_TYPE = reflectKeys([
  "MCQ",
  "MRQ",
  "MPQ",
  "REC",
  "FREE",
  "FILE",
  "FILL",
  "DND",
  "EEQ",
]);
export const QUESTION_WITH_DETAILS = ["REC", "FREE", "FILE", "DND"];
export const QUESTION_WITH_OPTIONS = ["MCQ", "MRQ", "FILL", "DND", "EEQ"];
export const QUESTION_WITH_STANDALONE_FEEDBACK = ["MCQ", "MRQ", "FILL", "DND", "EEQ"];
// TODO: to unify and regression test. also exists in types/lesson.ts
export const OPTION_DISPLAY_MODE = {
  MCQ: { value: "MCQ", label: "poll_type_mcq_label" },
  SLIDER: { value: "SLIDER", label: "poll_type_slider_label" },
  TEXT_INPUT: { value: "TEXTINPUT", label: "option_display_type_text_input" },
  SHARED_DROPDOWN: { value: "SHARED_DROPDOWN", label: "option_display_type_shared_dropdown" },
  DROPDOWN: { value: "DROPDOWN", label: "option_display_type_dropdown" },
};

export const ATTEMPTABLE_TYPE = reflectKeys(["LESSON", "ASSIGNMENT_LESSON"]) as {
  LESSON: string;
  ASSIGNMENT_LESSON: string;
};
export const PREVIEW_MODE = reflectKeys(["APPROVEDCOPY", "WORKINGCOPY"]);
export const ATTEMPT_TYPE = reflectKeys(["ATTEMPT", "PREVIEW"]);
export const RESOURCE_ATTEMPT_TYPE = reflectKeys(["LESSON", "ASSIGNMENT", "QUIZ"]);

export type AttemptableType = (typeof ATTEMPTABLE_TYPE)[keyof typeof ATTEMPTABLE_TYPE];
export type AttemptType = (typeof ATTEMPT_TYPE)[keyof typeof ATTEMPT_TYPE];
export type ResourceAttemptType =
  (typeof RESOURCE_ATTEMPT_TYPE)[keyof typeof RESOURCE_ATTEMPT_TYPE];

export const MARKING_STATUS = reflectKeys(["RELEASE", "PENDING_MARK", "MARKED"]);
export const ATTEMPT_STATUS = reflectKeys(["SUBMITTED", "ATTEMPTED", "PENDING", "COMPLETED"]);
export type AttemptStatus = (typeof ATTEMPT_STATUS)[keyof typeof ATTEMPT_STATUS];

export const ACTIVITY_TYPE = {
  SECTION: "SECTION",
  ACTIVITY: "ACTIVITY",
  ACTIVITY_QUIZ: "ACTIVITY_QUIZ",
};

export const ATTEMPT_RESULT_STATUS = reflectKeys([
  "CORRECT",
  "PARTIALLY_CORRECT",
  "WRONG",
  "SUBMITTED",
  "PARTIALLY_SUBMITTED",
  "NOT_SUBMITTED",
  "DRAFT",
]);

export const QUIZ_TEST_MODE = reflectKeys([
  "PRACTICE",
  "TEST_AUTO_RELEASE",
  "TEST_TEACHER_RELEASE",
]);

export const QUIZ_TEST_MODE_TYPE = {
  PRACTICE: {
    name: "PROGRESSIVE",
    displayName: "activity_quiz_practice",
  },
  TEST_AUTO_RELEASE: {
    name: "AUTO",
    displayName: "activity_quiz_auto_release",
  },
  TEST_TEACHER_RELEASE: {
    name: "TEACHER_MARKED",
    displayName: "activity_quiz_manual_release",
  },
};

export const POLL_FETCH_INTERVAL = 15000;
export const TRQ_PAGE_PROGRESS_FETCH_INTERVAL = 30000;
export const CHECK_LOCK_INTERVAL = 30000; //60000;
export const ISSUE_LOCK_INTERVAL = 30000; //60000;
export const QUESTION_AUTOSAVE_INTERVAL = 3000;
export const UPDATE_LAST_ACCESSED_DATE_INTERVAL = 180000;

export const ACTIVITY_RESTRICTION = reflectKeys(["SUBGROUP", "NONE", "TEAM", "INDIVIDUAL"]);
export const SORT_OPTIONS = reflectKeys(["INDEX_NO", "EARLIEST_FIRST"]);
export const FILTER_OPTIONS = reflectKeys(["BOOKMARK"]);
export const ALPHABETS_LIST = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];
export const POLL_COLOR_LIST = [
  "#c14149",
  "#f3812f",
  "#fbbc04",
  "#67b717",
  "#20cdd8",
  "#2b63d9",
  "#a674df",
  "#fe8693",
  "#b6bdc5",
  "#3c4f65",
];
export const COMPLETION_STATUS = {
  NEW: "new",
  INCOMPLETE: "incomplete",
  COMPLETED: "completed",
};

export const PROGRESS_STATUS_OPTIONS = {
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  OVERDUE: "Overdue",
};

export const RESOURCE_NODE_TYPE = reflectKeys(["FOLDER"]);

export const COPY_ACTION_TYPE = reflectKeys([
  "COPY_ASSIGNMENT_LESSON",
  "COPY_MOE_LESSON",
  "REATTEMPT_ASSIGNMENT_LESSON",
]);

export const SPACE_VIEW = reflectKeys([
  "COMMON",
  "CONVERSATION_ROOM",
  "INDIVIDUAL",
  "AGGREGATE_ATTEMPT",
  "AGGREGATE_QUIZ_ATTEMPT",
  "MONITORING",
  "ACTIVITY_PAGE",
  "ALS_SECTION_ATTEMPT",
  "ABILITY_SNAPSHOT",
]);

export const ATTEMPT_EVENTBUS_EVENT = {
  COMPONENT_BLUR: "COMPONENT_BLUR",
  PROGRESS_FETCH_COMPLETE: "PROGRESS_FETCH_COMPLETE",
};

export const RULE_CONDITION_TYPE = {
  SCHEDULE: "SCHEDULE",
  ACCESSIBLE: "ACCESSIBLE",
  GROUP_ACCESS: "GROUP_ACCESS",
  TEAM_ACCESS: "TEAM_ACCESS",
  QUIZ_SCORE: "QUIZ_SCORE",
  INDIVIDUAL_ACCESS: "INDIVIDUAL_ACCESS",
  PREVIOUS_COMPLETION: "PREVIOUS_COMPLETION",
  RESOURCE_COMPLETION: "RESOURCE_COMPLETION",
  GAME_AWARD: "GAME_AWARD",
  GAME_CHOICE: "GAME_CHOICE",
  LEVEL_UP: "LEVEL_UP",
  QUIZ_RANK: "QUIZ_RANK",
};

export const UGC_VALIDATION_RESULT = {
  SCANNING: "ugc_scanning",
  VIRUS: "ugc_virus",
  EMPTY_SE_UGC: "empty.se.ugc",
  UGC_TIME_EXCEED: "ugc.time.exceed",
};

export const ERROR_CODE = {
  LESSON_NOT_FOUND: "LessonNotFound",
  ACCESS_DENIED: "AccessDenied",
  ASSIGNMENT_NOT_FOUND: "AssignmentNotFound",
  ASSIGNMENT_NOT_STARTED: "AssignmentNotStarted",
  CURRENT_USER_ACCESS_DENIED: "CurrentUserAccessDenied",
  ASSIGNMENT_ACCESS_DENIED: "AssignmentAccessDenied",
  ASSIGNMENT_PAUSED: "AssignmentPaused",
  ASSIGNMENT_SHARING_RESOURCE_ACCESS_DENIED: "AssignmentSharingResourceAccessDenied",
  OTHER_USER_ACCESS_DENIED: "OtherUserAccessDenied",
  ATTEMPT_USER_LOCK: "QuizAttemptUserLock",
  EMPTY_RESPONSE: "empty.response",
  PASSCODE_NOT_AUTHENTICATED: "passcode-not-authenticated",
  PENDING_FEEDBACK: "PendingFeedback",
};

export const RECOMMENDATION_TYPE = reflectKeys(["SURVEY", "ACTIVITY", "QUESTION", "OTHERS"]);

export const CONDITION_TYPE = {
  RESOURCE_STARTS: "RESOURCE_START",
  RESOURCE_COMPLETE: "RESOURCE_COMPLETION",
  LEVEL_UP: "LEVEL_UP",
  QUIZ_MARK_AWARD: "RESOURCE_SCORE",
  GAME_AWARD: "GAME_AWARD",
  QUIZ_RANK: "QUIZ_RANK",
  GAME_CHOICE: "GAME_CHOICE",
};

export const GAME_AWARD_TYPE = reflectKeys([
  "GAME_STORY",
  "ACHIEVEMENT",
  "GAME_AVATAR",
  "DIGITAL_BADGE",
]);

export const ASSIGNMENT_NOTIFICATION_POLLING_INTERVAL = 60000; // 1 mins

export const RECORDING_MAX_DURATION = {
  DEFAULT: 600000, //default
  SE_QUESTION: 300000,
};

export const ARQ_MAX_ATTACHMENTS = 10; // MAX 10 ATTACHMENTS FOR SAVE/DRAFT (SLS-8249)

export const MAX_CUSTOM_NOTIFICATION_MESSAGE_CHARACTERS = 50;

export const ASSIGNMENT_NOTIFICATION_TYPE = {
  GAME_AWARD: "GAME_AWARD",
  RELEASE_MARKS: "RELEASE_MARKS",
  MARKS_ZIP_FILES_DONE: "MARKS_ZIP_FILES_DONE",
  RESPONSE_ZIP_FILES_DONE: "RESPONSE_ZIP_FILES_DONE",
  UPDATE_TEACHER_ANNOTATION: "UPDATE_TEACHER_ANNOTATION",
};

export const ROUTES_ATTEMPT = {
  ASSESSMENT: {
    COVER_PAGE: "AssessmentAttemptCover",
    END_PAGE: "AssessmentAttemptEnd",
    SECTION_COVER_PAGE: "AssessmentAttemptSectionCover",
    SECTION_END_PAGE: "AssessmentAttemptSectionEnd",
    ACTIVITY_PAGE: "AssessmentAttemptActivityPage",
    COMMON_SPACE: "AssessmentAttemptCommonSpaceView",
    CONVERSATION_ROOM: "AssessmentAttemptConversationRoomView",
    PAUSE_PAGE: "AssessmentAttemptPaused",
    WAITING_ROOM: "AssessmentAttemptWaitingRoom",
    PASSCODE_PAGE: "AssessmentAttemptPasscode",
    NOT_FOUND: "AssessmentAttemptNotFoundPage",
  },
  ASSIGNMENT: {
    COVER_PAGE: "AssignmentAttemptCover",
    END_PAGE: "AssignmentAttemptEnd",
    SECTION_COVER_PAGE: "AssignmentAttemptSectionCover",
    SECTION_END_PAGE: "AssignmentAttemptSectionEnd",
    ACTIVITY_PAGE: "AssignmentAttemptActivityPage",
    COMMON_SPACE: "AssignmentAttemptCommonSpaceView",
    CONVERSATION_ROOM: "AssignmentAttemptConversationRoomView",
    PAUSE_PAGE: "AssignmentAttemptPaused",
    WAITING_ROOM: "AssignmentAttemptWaitingRoom",
    PASSCODE_PAGE: "AssignmentAttemptPasscode",
    NOT_FOUND: "AssignmentAttemptNotFoundPage",
  },
  ALS_SESSION: {
    cover: "AlsSessionAttemptCover",
    end: "AlsSessionAttemptEnd",
    sectionCover: "AlsSessionAttemptSectionCover",
    sectionEnd: "AlsSessionAttemptSectionEnd",
    activity: "AlsSessionAttemptActivityPage",
    exit: "AlsLandingPage",
    notFound: "AlsSessionAttemptNotFoundPage",
    commonspace: "AlsSessionAttemptCommonSpaceView",
    conversationRoom: "AlsSessionAttemptConversationRoomView",
  },
  PERSONAL_LESSON: {
    cover: "PersonalLessonAttemptCover",
    end: "PersonalLessonAttemptEnd",
    sectionCover: "PersonalLessonAttemptSectionCover",
    sectionEnd: "PersonalLessonAttemptSectionEnd",
    activity: "PersonalLessonAttemptActivityPage",
    exit: "MyDriveHome",
    commonspace: "PersonalLessonAttemptCommonSpaceView",
    conversationRoom: "PersonalLessonAttemptConversationRoomView",
    passcode: "PersonalLessonAttemptPasscode",
    notFound: "PersonalLessonNotFoundPage",
  },
  STUDENT_GROUP_RESOURCE: {
    cover: "StudentGroupResourceAttemptCover",
    end: "StudentGroupResourceAttemptEnd",
    sectionCover: "StudentGroupResourceAttemptSectionCover",
    sectionEnd: "StudentGroupResourceAttemptSectionEnd",
    activity: "StudentGroupResourceAttemptActivityPage",
    exit: "StudentGroup",
    commonspace: "StudentGroupResourceAttemptCommonSpaceView",
    conversationRoom: "StudentGroupResourceAttemptConversationRoomView",
    paused: "StudentGroupResourceAttemptPaused",
    passcode: "StudentGroupResourceAttemptPasscode",
    notFound: "StudentGroupResourceAttemptNotFoundPage",
    exitQuery: { tab: "resources" },
  },
  SELF_STUDY: {
    cover: "SelfStudyAttemptCover",
    end: "SelfStudyAttemptEnd",
    sectionCover: "SelfStudyAttemptSectionCover",
    sectionEnd: "SelfStudyAttemptSectionEnd",
    activity: "SelfStudyAttemptActivityPage",
    exit: "FindMoeLibrary",
    commonspace: "SelfStudyAttemptCommonSpaceView",
    conversationRoom: "SelfStudyAttemptConversationRoomView",
    passcode: "SelfStudyAttemptPasscode",
    notFound: "SelfStudyAttemptNotFoundPage",
    unpublished: "SelfStudyAttemptUnpublishedPage",
  },
};

export const ROUTES_VIEW = {
  ASSESSMENT: {
    COVER_PAGE: "AssessmentViewCover",
    END_PAGE: "AssessmentViewEnd",
    SECTION_COVER_PAGE: "AssessmentViewSectionCover",
    SECTION_END_PAGE: "AssessmentViewSectionEnd",
    ACTIVITY_PAGE: "AssessmentViewActivityPage",
    COMMON_SPACE: "AssessmentViewCommonSpaceView",
    CONVERSATION_ROOM: "AssessmentViewConversationRoomView",
    AGGREGATE_ATTEMPT: "AssessmentMonitorAggregateAttemptView",
    ABILITY_SNAPSHOT: "AssessmentMonitorAbilitySnapshot",
    AGGREGATE_QUIZ_ATTEMPT: "AssessmentMonitorAggregateQuizAttempt",
    MONITORING: "AssessmentMonitor",
    NOT_FOUND: "AssessmentViewNotFoundPage",
    MODULE_PLAN: "AssessmentViewModulePlan",
  },
  ASSIGNMENT: {
    COVER_PAGE: "AssignmentViewCover",
    END_PAGE: "AssignmentViewEnd",
    SECTION_COVER_PAGE: "AssignmentViewSectionCover",
    SECTION_END_PAGE: "AssignmentViewSectionEnd",
    ACTIVITY_PAGE: "AssignmentViewActivityPage",
    COMMON_SPACE: "AssignmentViewCommonSpaceView",
    CONVERSATION_ROOM: "AssignmentViewConversationRoomView",
    AGGREGATE_ATTEMPT: "AssignmentMonitorAggregateAttemptView",
    ABILITY_SNAPSHOT: "AssignmentMonitorAbilitySnapshot",
    AGGREGATE_QUIZ_ATTEMPT: "AssignmentMonitorAggregateQuizAttempt",
    MONITORING: "AssignmentMonitor",
    NOT_FOUND: "AssignmentViewNotFoundPage",
    MODULE_PLAN: "AssignmentViewModulePlan",
    DISCUSSION_DAT_ANALYSE: "AssignmentViewDiscussionDatAnalyse",
    DISCUSSION_DAT_RESULT: "AssignmentViewDiscussionDatAnalyseResult",
    DISCUSSION_DAT_INSIGHT: "AssignmentViewDiscussionDatInsight",
    DATA_ASSISTANT: "DataAssistant",
    DATA_ASSISTANT_ADAPTIVE: "DataAssistantAdaptive",
  },
  ALS_SESSION: {
    cover: "AlsSessionViewCover",
    end: "AlsSessionViewEnd",
    sectionCover: "AlsSessionViewSectionCover",
    sectionEnd: "AlsSessionViewSectionEnd",
    activity: "AlsSessionViewActivityPage",
    exit: "AlsLandingPage",
    notFound: "AlsSessionViewNotFoundPage",
  },
};

export const ARQ_AUDIO_ATTACHMENTS = {
  SUBMIT: 1, // DEFAULT - submit 1 audio submission only
  SAVE: 10, // auto-save | save as draft | save upon navigate
};

export const LFA_STATUS = {
  REQUESTED: "REQUESTED",
  COMPLETE: "COMPLETE",
  ERROR: "ERROR",
  FAIL: "FAIL",
};

export const LAC_STATUS = reflectKeys(["PROCESSING", "READY", "ERROR"]);

export const LFA_CHECKBOX_TYPES = reflectKeys(["FEEDBACK", "MARKS"]);

export const BAR_CHART_TYPE = {
  MCQ: "MCQ",
  POLL: "POLL",
};

export const RESPONSE_SIZES_STRING = {
  SIZE_S: "short",
  SIZE_M: "medium",
  SIZE_1: "long",
  SIZE_2: "long",
  SIZE_3: "long",
  SIZE_4: "long",
  SIZE_5: "long",
  SIZE_6: "long",
  SIZE_7: "long",
  SIZE_8: "long",
  SIZE_9: "long",
};

export const RESPONSE_SIZES_MAP = {
  SHORT: "SIZE_S",
  MEDIUM: "SIZE_M",
  LONG: "SIZE_1",
};

export const ROLE_IN_GROUP = {
  GROUP_OWNER: "GROUP_OWNER",
  CO_TEACHER: "CO_TEACHER",
  LIAISON_TEACHER: "LIAISON_TEACHER",
  OBSERVER: "OBSERVER",
  STUDENT: "STUDENT", // only for front end usage as there is no such constant in the backend.
};

export const EMPTY_HINT_PLACEHOLDER = "emptyHint";

export const ALS_SESSION_DATA_STORAGE_KEY = "AlsSessionData";

export const HEADER_BREADCRUMB_NAVIGIATION_TYPE = reflectKeys([
  "LESSON_TITLE",
  "SECTION_TITLE",
  "ACTIVITY_TITLE",
  "QUESTION_NUMBER",
  "DAT",
]);

export const DAT_ROUTE_NAMES = [
  "AssignmentViewDiscussionDatAnalyse",
  "AssignmentViewDiscussionDatAnalyseResult",
  "AssignmentViewDiscussionDatInsight",
  "DataAssistant",
  "DataAssistantAdaptive",
] as const;

export const FEEDBACK_REFERENCE = {
  ERROR_TAG: "Error Tags",
  SUGGESTED_ANSWER: "Suggested Answer",
  GRADING_RUBRIC: "Rubrics",
};

export const APP_USAGE = {
  FEEDBACK: "feedback",
  FEEDBACK_AND_MARKING: "feedback or marks",
};

export const FORUM_TOPIC_LAYOUT = reflectKeys(["COLUMNS", "DEFAULT"]);

export const AUTO_SAVE_INDICATOR_STATE = {
  IDLE: "idle",
  SAVING: "saving",
  SAVED: "saved",
  ERROR: "error",
} as const;

export type AutoSaveIndicatorState =
  (typeof AUTO_SAVE_INDICATOR_STATE)[keyof typeof AUTO_SAVE_INDICATOR_STATE];

export const AUTOSAVE_MIN_SAVING_DURATION_MS = 1000;
export const AUTOSAVE_RESULT_DISPLAY_DURATION_MS = 3000;
